import { toast } from 'sonner'
import { demoLoginHints } from './seed/customers'
import { buildSeedTables, FIRST_ORDER_NUMBER } from './seed/build'
import { nowStamp, shiftDate, today } from '../core/format/dates'
import { readLocalJson, writeLocalJson } from '../core/storage'
import type { Account, AccountOrder, Appointment } from '../core/types/account'
import type { AdminContent, BookingBlock, InboxLead, InboxOrder, OrderEvent, OrderFulfilment, OrderRefund } from '../core/types/adminRequests'
import type { BookingRequest } from '../core/types/requests'
import type { AgendaBlock, AppointmentOutcome, AppointmentProducts } from '../core/types/agenda'
import type { LedgerEntry, Voucher } from '../core/types/vouchers'
import type { CashDayClose, KuurStop, Sale, SaleReturn } from '../core/types/kassa'
import type { PurchaseReceipt, StockCount, StockItemKind, StockMovement } from '../core/types/stock'
import type { AcquisitionSource } from '../core/types/insights'

/**
 * DEMO — de database van de demo-backend, in de vorm die de echte server
 * straks ook heeft: losse tabellen, aan elkaar gekoppeld met ids.
 *
 *   customers     klant + huidplan, abonnement, bezoeken, kuur, fotodagboek (1-op-1 per klant)
 *   orders        elke bestelling één keer; Mijn omgeving én de inbox zijn er een weergave van
 *   appointments  elke afspraak één keer, van een klant (customerId) of een gast (guest)
 *   notes         interne notities per klant
 *   leads         huidadvies-aanvragen uit de quiz
 *   newsletter    aanmeldingen
 *   content       het laatst opgeslagen beheer-concept
 *   vouchers      cadeaubonnen met hun grootboek; credits: tegoed per klant
 *   sales         bonnen uit de kassa; cashDays: dagafsluitingen; kuurStops: gestopte kuren (creditnota's)
 *   stockItems    salonvoorraad, inkoopprijs, verbruiksartikelen; stockMovements: het voorraadlog
 *   stockReceipts geboekte inkoopfacturen; stockCounts: tellingen per plek
 *
 * Opgeslagen in localStorage met een schemaversie. Een andere versie, of iets wat
 * niet klopt, wordt opnieuw opgebouwd uit de voorbeelddata (`seed/build.ts`).
 * Alleen demo/*Backend.ts gebruikt dit bestand.
 */

// ——— Schema ———

// v5: één adresvorm (straat en huisnummer apart, plus land).
// v6: voorbeeld van een mislukte abonnementsbetaling (Eva, F20).
// v7: agenda — afspraken blijven bewaard met hun afloop, plus blokkades.
// v8: `customer.hasPassword` (wachtwoord instellen of wijzigen).
// v9: bestellingen en omzet — btw per regel, betaalmoment, tijdlijn, terugbetalingen, annuleren, ±60 voorbeeldorders.
// v10: cadeaubonnen en klanttegoed (grootboek), `voucherCents` op bestellingen.
// v12: vooruitbetaalde kuur (`course.prepaid`), kuur in de webshop.
// v13: kassa — bonnen (`sales`), dagafsluitingen (`cashDays`), `saleId` op afspraken.
// v14: voorraad per plek — `stockItems` (salon, inkoopprijs, verbruik) en `stockMovements`.
// v15: inkoopfacturen (`stockReceipts`) en tellingen (`stockCounts`).
// v16: herkomst ("Hoe kwam je bij ons?") bij klanten, bestellingen, gastafspraken en bonnen.
// v17: gestopte kuren met hun creditnota (`kuurStops`).
// v18: retouren op kassabonnen met hun creditnota (`saleReturns`).
// Een oudere versie wordt niet gemigreerd maar opnieuw opgebouwd uit de voorbeelddata (het is een demo).
export const SCHEMA_VERSION = 18

export type CustomerRow = Omit<Account, 'orders' | 'appointments'>

export type OrderRow = {
  id: string
  /** Idempotentie: dezelfde requestId = dezelfde bestelling. */
  requestId?: string
  receivedAt: string
  customerId?: string
  name: string
  city: string
  fulfilment: OrderFulfilment
  /** Ingevuld door Sarah bij "Verzonden". */
  tracking?: AccountOrder['tracking']
  request: InboxOrder['order']
  payment: InboxOrder['payment']
  /** Vastgelegd bij het plaatsen, zoals de server het doet. */
  gift: string | null
  /** Zoals betaald (historisch, voor de factuur), met het btw-tarief van het moment van verkoop. */
  lines: OrderLine[]
  shipping: number
  total?: number
  viaSubscription: boolean
  /** Het nette, doorlopende bestelnummer: pas bij betalen (`numberOrder`), daarvoor leeg. */
  number?: number
  /** Moment van betalen; leeg bij een betaalde order = `receivedAt` (de demo betaalt direct). */
  paidAt?: string
  /** Wat er na het plaatsen gebeurde (ingepakt, verzonden, bezorgd, terugbetaald, geannuleerd). */
  events?: OrderEvent[]
  refunds?: (OrderRefund & { requestId: string })[]
  cancelledAt?: string
  /** Idempotentie van annuleren: dezelfde requestId nog eens = hetzelfde antwoord. */
  cancelRequestId?: string
  /** Van een cadeaubon afgehaald, in centen. */
  voucherCents?: number
  /** Er zit een kuur in die niet automatisch op een account kon (demo/kuurOrders.ts). Weg zodra Sarah hem vastlegt. */
  kuurUnlinked?: boolean
  /** "Hoe kwam je bij ons?" op de bedankpagina (één keer per order). */
  acquisitionSource?: AcquisitionSource
}

/** Een grootboekregel zoals bewaard: met de `requestId` van de actie (idempotentie). */
export type LedgerRow = LedgerEntry & { requestId?: string }

/** Een cadeaubon zoals bewaard. Saldo en status zijn altijd afgeleid uit het grootboek, nooit opgeslagen. */
export type VoucherRow = Omit<Voucher, 'codeLast4' | 'balanceCents' | 'status' | 'ledger'> & { requestId?: string; ledger: LedgerRow[] }

/** Tegoed van een klant: één regel per boeking. */
export type CreditRow = LedgerRow & { customerId: string }

/** Eén bestelregel zoals bewaard. `vatRate` leeg (oudere regel) = het tarief van het product nu. */
export type OrderLine = AccountOrder['lines'][number] & { vatRate?: number }

/**
 * Eén afspraak, voor altijd. Afgerond, weggebleven of geannuleerd = `outcome` gezet; zo'n rij
 * staat niet meer in Mijn omgeving, maar wel in de agenda (zoals de server het bewaart).
 */
export type AppointmentRow = Appointment & {
  receivedAt: string
  /** Leeg = een gast; dan staat `guest` ingevuld. */
  customerId?: string
  guest?: Pick<BookingRequest, 'name' | 'email' | 'firstVisit' | 'acquisitionSource'> & { phone?: string }
  staffId?: string
  /** Leeg = de duur van de behandeling uit de catalogus. */
  durationMinutes?: number
  outcome?: AppointmentOutcome
  cancelledBy?: 'klant' | 'salon'
  products?: AppointmentProducts
  treatmentNote?: string
  /** Idempotentie voor afspraken die Sarah in de agenda maakt. */
  requestId?: string
  /** De bon waarmee deze afspraak in de kassa is afgerekend. */
  saleId?: string
  /** "Binnen" (Mijn dag). */
  arrivedAt?: string
  /** Wie een aanvraag bevestigde of een andere tijd voorstelde. */
  handledBy?: { staffId: string; name: string; accountId?: string; action: 'bevestigd' | 'voorgesteld' }
}

/** Een bon uit de kassa zoals bewaard, met de `requestId` van het afrekenen (en van een correctie). */
export type SaleRow = Sale & { requestId: string; voidRequestId?: string; voucherId?: string; acquisitionSource?: AcquisitionSource }

/** Een dagafsluiting zoals bewaard. */
export type CashDayRow = CashDayClose & { requestId: string }

/** Een gestopte kuur (creditnota) zoals bewaard. */
export type KuurStopRow = KuurStop & { requestId: string }

/** Een retour op een bon (creditnota) zoals bewaard. */
export type SaleReturnRow = SaleReturn & { requestId: string }

/**
 * Voorraad buiten de catalogus: salonvoorraad, inkoopprijs en verbruiksartikelen. Bij een
 * catalogusproduct komt de webshopvoorraad uit de producttabel (`stockOverrides`), niet hieruit.
 */
export type StockItemRow = {
  id: string
  kind: StockItemKind
  /** Alleen bij `verbruik`; een catalogusproduct heeft zijn naam in de catalogus. */
  name?: string
  brand?: string
  salon: number | null
  /** Alleen bij `verbruik`: de voorraad op de webshopplek (opslag). */
  webshop?: number | null
  costPriceCents?: number
  requestId?: string
}

export type StockMovementRow = StockMovement & { requestId?: string }

/** Een geboekte inkoopfactuur. De bijlage zelf bewaart de demo niet (te groot voor de browser); alleen naam, grootte en soort. */
export type StockReceiptRow = Omit<PurchaseReceipt, 'file'> & { requestId: string; file?: { name: string; size: number; type: string } }

export type StockCountRow = StockCount & { requestId: string }

export type Tables = {
  customers: CustomerRow[]
  orders: OrderRow[]
  appointments: AppointmentRow[]
  blocks: AgendaBlock[]
  notes: Record<string, string>
  /** "Niet online laten boeken" per klant-id. Optioneel: een oudere opgeslagen demo heeft hem niet. */
  bookingBlocks?: Record<string, BookingBlock>
  leads: InboxLead[]
  newsletter: {
    base: { subscribers: number; lastWeek: number }
    /** Dubbele opt-in: pas met `confirmedAt` mag er een nieuwsbrief heen. `token` staat in de bevestigingsmail. */
    signups: { email: string; at: string; token?: string; confirmedAt?: string }[]
  }
  content: { fingerprint: string; value: AdminContent } | null
  vouchers: VoucherRow[]
  credits: CreditRow[]
  sales: SaleRow[]
  cashDays: CashDayRow[]
  kuurStops: KuurStopRow[]
  saleReturns: SaleReturnRow[]
  stockItems: StockItemRow[]
  stockMovements: StockMovementRow[]
  stockReceipts: StockReceiptRow[]
  stockCounts: StockCountRow[]
}

type Stored = { version: typeof SCHEMA_VERSION; tables: Tables }

const DB_KEY = 'bb-demo-db'
const SESSION_KEY = 'bb-demo-session-v2'
const SESSION_AT_KEY = 'bb-demo-session-at'
const ADMIN_SESSION_KEY = 'bb-demo-admin-session'
const STOCK_KEY = 'bb-demo-stock'

// ——— Hulpjes ———

const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v)) as T
const norm = (e: string) => e.trim().toLowerCase()
const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null
const isArray = (v: unknown): v is unknown[] => Array.isArray(v)

const accountStatusOf = (f: OrderFulfilment): AccountOrder['status'] =>
  f === 'bezorgd' ? 'Bezorgd' : f === 'verzonden' ? 'Onderweg' : 'In behandeling'

// ——— Controle bij het laden ———

function isTables(v: unknown): v is Tables {
  if (!isObject(v)) return false
  const { customers, orders, appointments, blocks, notes, leads, newsletter, vouchers, credits, sales, cashDays, kuurStops, saleReturns, stockItems, stockMovements, stockReceipts, stockCounts } = v
  return (
    isArray(saleReturns) &&
    saleReturns.every((r) => isObject(r) && typeof r.id === 'string' && typeof r.saleId === 'string' && isArray(r.lines)) &&
    isArray(kuurStops) &&
    kuurStops.every((k) => isObject(k) && typeof k.id === 'string' && typeof k.refundCents === 'number') &&
    isArray(stockReceipts) &&
    stockReceipts.every((r) => isObject(r) && typeof r.id === 'string' && isArray(r.lines)) &&
    isArray(stockCounts) &&
    stockCounts.every((c) => isObject(c) && typeof c.id === 'string' && typeof c.at === 'string') &&
    isArray(stockItems) &&
    stockItems.every((s) => isObject(s) && typeof s.id === 'string') &&
    isArray(stockMovements) &&
    stockMovements.every((m) => isObject(m) && typeof m.itemId === 'string' && typeof m.qty === 'number') &&
    isArray(sales) &&
    sales.every((s) => isObject(s) && typeof s.id === 'string' && isArray(s.lines)) &&
    isArray(cashDays) &&
    isArray(blocks) &&
    isArray(vouchers) &&
    vouchers.every((x) => isObject(x) && typeof x.id === 'string' && isArray(x.ledger)) &&
    isArray(credits) &&
    isArray(customers) &&
    customers.every((c) => isObject(c) && isObject(c.customer) && typeof c.customer.id === 'string') &&
    isArray(orders) &&
    orders.every((o) => isObject(o) && typeof o.id === 'string' && isArray(o.lines)) &&
    isArray(appointments) &&
    appointments.every((a) => isObject(a) && typeof a.id === 'string') &&
    isObject(notes) &&
    isArray(leads) &&
    isObject(newsletter) &&
    isArray(newsletter.signups)
  )
}

/** Opgeslagen stand → tabellen, of `null` bij een andere schemaversie of een vorm die niet klopt. */
function fromStorage(raw: unknown): Tables | null {
  return isObject(raw) && raw.version === SCHEMA_VERSION && isTables(raw.tables) ? raw.tables : null
}

// ——— Opslag ———

let memory: Tables | null = null

function read(key: string): unknown {
  try {
    const raw = localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as unknown) : null
  } catch {
    return null
  }
}

function load(): Tables {
  if (memory) return memory
  memory = fromStorage(read(DB_KEY)) ?? buildSeedTables()
  persist()
  return memory
}

/** Eén melding per sessie: daarna weet je het, en elke volgende wijziging zou hem herhalen. */
let warnedNotSaved = false

function persist() {
  try {
    if (memory) localStorage.setItem(DB_KEY, JSON.stringify({ version: SCHEMA_VERSION, tables: memory } satisfies Stored))
  } catch {
    // Privémodus of vol (zo'n 5 MB; foto's als data-URL vullen dat snel). De demo werkt verder in het
    // geheugen, maar herladen gooit de wijzigingen weg: dat mag niet stil gebeuren.
    if (warnedNotSaved) return
    warnedNotSaved = true
    toast.error('De demo kan je wijzigingen niet meer bewaren', {
      description: 'De opslag van deze browser is vol (vaak door foto’s). Tot je herlaadt werkt alles gewoon; daarna is het weg.',
      duration: 12_000,
    })
  }
}

function write(fn: (t: Tables) => void) {
  fn(load())
  persist()
}

// ——— Weergaven ———

const toAccountOrder = (o: OrderRow): AccountOrder => ({
  id: o.id,
  number: o.number ? String(o.number) : null,
  date: o.receivedAt.slice(0, 10),
  lines: o.lines,
  shipping: o.shipping,
  total: o.total,
  status: accountStatusOf(o.fulfilment),
  ...(o.tracking && o.fulfilment !== 'nieuw' && o.fulfilment !== 'ingepakt' ? { tracking: o.tracking } : {}),
  viaSubscription: o.viaSubscription,
  ...(o.cancelledAt ? { cancelledAt: o.cancelledAt.slice(0, 10) } : {}),
})

export const toInboxOrder = (o: OrderRow): InboxOrder => ({
  id: o.id,
  receivedAt: o.receivedAt,
  customerId: o.customerId,
  name: o.name,
  city: o.city,
  status: o.fulfilment === 'bezorgd' ? 'verzonden' : o.fulfilment,
  ...(o.tracking ? { tracking: o.tracking } : {}),
  payment: o.payment,
  gift: o.gift,
  totalCents: Math.round((o.total ?? o.lines.reduce((s, l) => s + l.price * l.qty, 0) + o.shipping) * 100),
  order: o.request,
  ...(o.kuurUnlinked ? { kuurToLink: true } : {}),
})

/** Wat de klant van een afspraak ziet: zonder interne notitie, producten of afloop. */
const toAppointment = (row: AppointmentRow): Appointment => ({
  id: row.id,
  treatmentSlug: row.treatmentSlug,
  date: row.date,
  time: row.time,
  kind: row.kind,
  status: row.status,
  ...(row.request ? { request: row.request } : {}),
  ...(row.session ? { session: row.session } : {}),
})

function assemble(row: CustomerRow, t: Tables): Account {
  const id = row.customer.id
  return clone({
    ...row,
    // Zoals de server: alleen betaalde bestellingen in Mijn omgeving. Een mislukte
    // abonnementsbetaling staat als melding bovenaan (Subscription.paymentIssue).
    orders: t.orders
      .filter((o) => o.customerId === id && o.payment.status === 'betaald')
      .sort((a, b) => b.receivedAt.localeCompare(a.receivedAt))
      .map(toAccountOrder),
    appointments: t.appointments.filter((a) => a.customerId === id && !a.outcome).map(toAppointment),
  })
}

// ——— Toegang ———

export const db = {
  accounts(): Account[] {
    const t = load()
    return t.customers.map((row) => assemble(row, t))
  },
  account(id: string): Account | null {
    const t = load()
    const row = t.customers.find((x) => x.customer.id === id)
    return row ? assemble(row, t) : null
  },
  accountByEmail(email: string): Account | null {
    // Klanten zonder e-mailadres (Sarah maakte ze aan) zijn nooit "dezelfde" als een lege invoer.
    if (!norm(email)) return null
    const t = load()
    const row = t.customers.find((x) => norm(x.customer.email) === norm(email))
    return row ? assemble(row, t) : null
  },

  /**
   * Een account terugschrijven: de klantrij en de afspraken van deze klant.
   * Bestellingen gaan hier NIET mee; die lopen via `putOrder`/`changeOrder`.
   */
  putAccount(account: Account) {
    const { orders: _orders, appointments, ...row } = clone(account)
    const id = row.customer.id
    write((t) => {
      const i = t.customers.findIndex((x) => x.customer.id === id)
      if (i >= 0) t.customers[i] = row
      else t.customers.unshift(row)

      // Alleen de open afspraken horen bij het account; afgelopen rijen (met `outcome`) blijven staan.
      const previous = new Map(t.appointments.filter((a) => a.customerId === id && !a.outcome).map((a) => [a.id, a]))
      const next = appointments.map<AppointmentRow>((x) => {
        const before = previous.get(x.id)
        // Nieuw verzoek (of opnieuw verzet): dan telt het vanaf nu in de inbox.
        const renewed = x.status === 'aanvraag' && (before?.status !== 'aanvraag' || before.request !== x.request)
        // Agenda-velden (behandelaar, duur) gaan niet mee in het account: van de vorige rij overnemen.
        const { request: _r, session: _s, ...kept }: Partial<AppointmentRow> = before ?? {}
        return { ...kept, ...x, customerId: id, receivedAt: !before || renewed ? nowStamp() : before.receivedAt }
      })
      // Een open afspraak die uit het account verdwijnt, heeft de klant geannuleerd: blijft in de agenda zichtbaar.
      const kept = new Set(next.map((a) => a.id))
      const cancelled = [...previous.values()]
        .filter((a) => !kept.has(a.id))
        .map<AppointmentRow>((a) => ({ ...a, outcome: 'geannuleerd', cancelledBy: 'klant' }))
      t.appointments = [...t.appointments.filter((a) => a.customerId !== id || a.outcome), ...cancelled, ...next]
    })
  },
  /**
   * Account verwijderen zoals de server het doet (docs/LARAVEL.md §12.6): klantrij, dagboek en
   * afspraken weg; bestellingen blijven voor de boekhouding (7 jaar) maar zonder koppeling.
   */
  removeAccount(id: string) {
    write((t) => {
      t.customers = t.customers.filter((x) => x.customer.id !== id)
      t.appointments = t.appointments.filter((a) => a.customerId !== id)
      t.orders = t.orders.map((o) => (o.customerId === id ? { ...o, customerId: undefined } : o))
    })
  },
  /**
   * Twee dossiers samenvoegen zoals de server het in één transactie doet: alles van `sourceId`
   * (bestellingen, alle afspraken, tegoed, kassabonnen, kuurstops, retouren, notitie, boekblokkade)
   * gaat naar `targetId`; daarna verdwijnt de bronrij en staat `merged` als het doeldossier.
   */
  mergeCustomer(targetId: string, sourceId: string, merged: Account, internalNote: string) {
    const { orders: _o, appointments: _a, ...row } = clone(merged)
    const repoint = <T extends { customerId?: string }>(rows: T[]): T[] =>
      rows.map((r) => (r.customerId === sourceId ? { ...r, customerId: targetId } : r))
    write((t) => {
      t.customers = t.customers.filter((x) => x.customer.id !== sourceId).map((x) => (x.customer.id === targetId ? row : x))
      t.orders = repoint(t.orders)
      t.appointments = repoint(t.appointments)
      t.credits = repoint(t.credits)
      t.sales = repoint(t.sales)
      t.kuurStops = repoint(t.kuurStops)
      t.saleReturns = repoint(t.saleReturns)
      t.notes = { ...t.notes, [targetId]: internalNote }
      delete t.notes[sourceId]
      if (t.bookingBlocks?.[sourceId]) {
        t.bookingBlocks = { [targetId]: t.bookingBlocks[sourceId], ...t.bookingBlocks }
        delete t.bookingBlocks[sourceId]
      }
    })
  },
  /** Past één account aan en geeft terug wat de functie teruggeeft; `null` als de klant niet bestaat. */
  changeAccount<T>(id: string, fn: (a: Account) => [Account, T]): T | null {
    const current = db.account(id)
    if (!current) return null
    const [next, result] = fn(current)
    db.putAccount(next)
    return result
  },

  orders: (): OrderRow[] => clone(load().orders),
  orderByRequest: (requestId: string): OrderRow | null =>
    clone(load().orders.find((o) => o.requestId === requestId) ?? null),
  putOrder(order: OrderRow) {
    write((t) => {
      t.orders = [clone(order), ...t.orders.filter((o) => o.id !== order.id)]
    })
  },
  changeOrder(id: string, fn: (o: OrderRow) => OrderRow): OrderRow | null {
    const current = load().orders.find((o) => o.id === id)
    if (!current) return null
    const updated = fn(clone(current))
    write((t) => {
      t.orders = t.orders.map((o) => (o.id === id ? updated : o))
    })
    return clone(updated)
  },
  /**
   * Geeft een betaalde bestelling haar bestelnummer (vanaf 1001, doorlopend, zonder gaten),
   * zoals de server het bij de betaalde webhook doet. Al genummerd: hetzelfde nummer terug.
   */
  numberOrder(id: string): number | null {
    const current = load().orders.find((o) => o.id === id)
    if (!current) return null
    if (current.number) return current.number
    const next = Math.max(FIRST_ORDER_NUMBER - 1, ...load().orders.map((o) => o.number ?? 0)) + 1
    write((t) => {
      t.orders = t.orders.map((o) => (o.id === id ? { ...o, number: next } : o))
    })
    return next
  },

  appointments: (): AppointmentRow[] => clone(load().appointments),
  addAppointment(row: AppointmentRow) {
    write((t) => {
      t.appointments = [...t.appointments, clone(row)]
    })
  },
  changeAppointment(id: string, fn: (a: AppointmentRow) => AppointmentRow): AppointmentRow | null {
    const current = load().appointments.find((a) => a.id === id)
    if (!current) return null
    const updated = fn(clone(current))
    write((t) => {
      t.appointments = t.appointments.map((a) => (a.id === id ? updated : a))
    })
    return clone(updated)
  },

  appointmentByRequest: (requestId: string): AppointmentRow | null =>
    clone(load().appointments.find((a) => a.requestId === requestId) ?? null),

  blocks: (): AgendaBlock[] => clone(load().blocks),
  addBlock(block: AgendaBlock) {
    write((t) => {
      t.blocks = [...t.blocks, clone(block)]
    })
  },
  /** `false` als de blokkade al weg was. */
  removeBlock(id: string): boolean {
    const known = load().blocks.some((b) => b.id === id)
    if (known) {
      write((t) => {
        t.blocks = t.blocks.filter((b) => b.id !== id)
      })
    }
    return known
  },

  bookingBlock: (id: string): BookingBlock | null => clone(load().bookingBlocks?.[id] ?? null),
  setBookingBlock(id: string, block: BookingBlock | null) {
    write((t) => {
      const { [id]: _old, ...rest } = t.bookingBlocks ?? {}
      t.bookingBlocks = block ? { ...rest, [id]: block } : rest
    })
  },

  note: (id: string) => load().notes[id] ?? '',
  setNote(id: string, note: string) {
    write((t) => {
      t.notes[id] = note
    })
  },

  leads: (): InboxLead[] => clone(load().leads),
  addLead(lead: InboxLead) {
    write((t) => {
      t.leads = [lead, ...t.leads]
    })
  },

  newsletter() {
    const { base } = load().newsletter
    // Alleen bevestigde aanmeldingen tellen als abonnee (dubbele opt-in).
    const signups = load().newsletter.signups.filter((s) => s.confirmedAt)
    // Datum zonder tijd: telt alles vanaf het begin van die dag (een tijdstempel sorteert erna).
    const weekAgo = shiftDate(today(), -7)
    return {
      subscribers: base.subscribers + signups.length,
      lastWeek: base.lastWeek + signups.filter((s) => s.at >= weekAgo).length,
    }
  },
  /** De losse aanmeldingen uit de footer (genormaliseerd e-mailadres, tijdstip). */
  newsletterSignups: () => clone(load().newsletter.signups),
  /** `false` als het adres al aangemeld was. Een nieuwe aanmelding wacht op bevestiging (eigen token). */
  addSignup(email: string): boolean {
    const known = load().newsletter.signups.some((s) => norm(s.email) === norm(email))
    if (!known) write((t) => t.newsletter.signups.push({ email: norm(email), at: nowStamp(), token: crypto.randomUUID() }))
    return !known
  },
  /** Klik op de bevestigingslink: alleen dan wordt de aanmelding bevestigd. `false` bij een onbekend token. */
  confirmSignup(token: string): boolean {
    const hit = load().newsletter.signups.find((s) => s.token === token)
    if (!hit) return false
    if (!hit.confirmedAt) {
      write((t) => {
        const row = t.newsletter.signups.find((s) => s.token === token)
        if (row) row.confirmedAt = nowStamp()
      })
    }
    return true
  },

  /** Het opgeslagen beheer-concept, alleen als het op dezelfde lokale data is gebaseerd. */
  content(fingerprint: string): AdminContent | null {
    const c = load().content
    return c && c.fingerprint === fingerprint ? clone(c.value) : null
  },
  /** Welke onderdelen het beheer laatst opsloeg (Instellingen › Koppelingen & privacy › Onderdelen), voor de demo-winkel. */
  savedFeatures(): unknown {
    return load().content?.value.site?.features ?? null
  },
  /** De merken zoals het beheer ze laatst opsloeg (o.a. korting aan de kassa), of `null`. */
  savedBrands() {
    const b = load().content?.value.catalog?.brands
    return b ? clone(b) : null
  },
  /** De instellingen die het beheer laatst opsloeg (zoals de server ze kent), of `null`. */
  savedPolicy() {
    const p = load().content?.value.site?.policy
    return p ? clone(p) : null
  },

  vouchers: (): VoucherRow[] => clone(load().vouchers),
  /** Alle bonnen in één keer aanpassen; de functie krijgt een kopie. */
  changeVouchers(fn: (rows: VoucherRow[]) => VoucherRow[]) {
    write((t) => {
      t.vouchers = fn(clone(t.vouchers))
    })
  },
  stockItems: (): StockItemRow[] => clone(load().stockItems),
  putStockItem(row: StockItemRow) {
    write((t) => {
      t.stockItems = [...t.stockItems.filter((s) => s.id !== row.id), clone(row)]
    })
  },
  stockMovements: (): StockMovementRow[] => clone(load().stockMovements),
  addStockMovements(rows: StockMovementRow[]) {
    if (rows.length === 0) return
    write((t) => {
      t.stockMovements = [...t.stockMovements, ...clone(rows)]
    })
  },

  stockReceipts: (): StockReceiptRow[] => clone(load().stockReceipts),
  addStockReceipt(row: StockReceiptRow) {
    write((t) => {
      t.stockReceipts = [...t.stockReceipts, clone(row)]
    })
  },
  stockCounts: (): StockCountRow[] => clone(load().stockCounts),
  addStockCount(row: StockCountRow) {
    write((t) => {
      t.stockCounts = [...t.stockCounts, clone(row)]
    })
  },

  sales: (): SaleRow[] => clone(load().sales),
  putSale(sale: SaleRow) {
    write((t) => {
      t.sales = [clone(sale), ...t.sales.filter((s) => s.id !== sale.id)]
    })
  },
  cashDay: (day: string): CashDayRow | null => clone(load().cashDays.find((d) => d.day === day) ?? null),
  cashDays: (): CashDayRow[] => clone(load().cashDays),
  putCashDay(row: CashDayRow) {
    write((t) => {
      t.cashDays = [clone(row), ...t.cashDays.filter((d) => d.day !== row.day)]
    })
  },

  kuurStops: (): KuurStopRow[] => clone(load().kuurStops),
  addKuurStop(row: KuurStopRow) {
    write((t) => {
      t.kuurStops = [clone(row), ...t.kuurStops]
    })
  },

  saleReturns: (): SaleReturnRow[] => clone(load().saleReturns),
  addSaleReturn(row: SaleReturnRow) {
    write((t) => {
      t.saleReturns = [...t.saleReturns, clone(row)]
    })
  },

  credits: (customerId: string): CreditRow[] => clone(load().credits.filter((c) => c.customerId === customerId)),
  addCredit(row: CreditRow) {
    write((t) => {
      t.credits = [...t.credits, clone(row)]
    })
  },
  setContent(fingerprint: string, value: AdminContent) {
    write((t) => {
      t.content = { fingerprint, value: clone(value) }
    })
  },

  /**
   * Voorraad die via het beheer is aangepast, los van het concept (zoals de server hem in de
   * producttabel bewaart). Overschrijft de voorraad uit het concept of de lokale data.
   */
  stockOverrides: (): Record<string, number | null> => readLocalJson<Record<string, number | null>>(STOCK_KEY, {}),
  setStock(productId: string, stock: number | null) {
    writeLocalJson(STOCK_KEY, { ...readLocalJson<Record<string, number | null>>(STOCK_KEY, {}), [productId]: stock })
  },

  /**
   * Voor het inlogscherm: elke klant uit de demo met een wachtwoord, met een korte uitleg.
   * Accounts die alleen via een inloglink bestaan (`hasPassword: false`) kunnen niet met DEMO_PASSWORD in.
   */
  logins: () =>
    load()
      .customers.filter((c) => c.customer.hasPassword)
      .map((c) => ({
      email: c.customer.email,
      label: c.customer.firstName,
      description: demoLoginHints[c.customer.id] ?? 'Nieuw account',
    })),

  // ——— Sessies (los van de tabellen, net als cookies) ———
  sessionId(): string | null {
    try {
      return localStorage.getItem(SESSION_KEY)
    } catch {
      return null
    }
  },
  setSession(id: string | null) {
    try {
      if (id) {
        localStorage.setItem(SESSION_KEY, id)
        // Moment van inloggen: voor "recente login" (423 `reauth_required`), zoals Laravel `password.confirm`.
        localStorage.setItem(SESSION_AT_KEY, String(Date.now()))
      } else {
        localStorage.removeItem(SESSION_KEY)
        localStorage.removeItem(SESSION_AT_KEY)
      }
    } catch {
      /* niet erg */
    }
  },
  /** Wanneer de huidige klantsessie begon (ms), of `null` als dat onbekend is. */
  sessionStartedAt(): number | null {
    try {
      const at = Number(localStorage.getItem(SESSION_AT_KEY))
      return Number.isFinite(at) && at > 0 ? at : null
    } catch {
      return null
    }
  },
  /** Per tabblad, net als een echte beheer-login. */
  adminSession(): boolean {
    try {
      return sessionStorage.getItem(ADMIN_SESSION_KEY) === '1'
    } catch {
      return false
    }
  },
  setAdminSession(on: boolean) {
    try {
      if (on) sessionStorage.setItem(ADMIN_SESSION_KEY, '1')
      else sessionStorage.removeItem(ADMIN_SESSION_KEY)
    } catch {
      /* niet erg */
    }
  },
}
